import React from "react";
import { FaExternalLinkAlt } from "react-icons/fa";
import { ImFileText } from "react-icons/im";
import { Link } from "react-router-dom";
import Loading from "../Loader/Loading";
import backendCommerceCover from "../../Images/cover-backend-commerce.svg";
import posInventoryCover from "../../Images/cover-pos-inventory.svg";
import warrantyServiceCover from "../../Images/cover-warranty-service.svg";
import communityQuizCover from "../../Images/cover-community-quiz.svg";
import donationAnalyticsCover from "../../Images/cover-donation-analytics.svg";
import fullstackServiceCover from "../../Images/cover-fullstack-service.svg";

const FALLBACK_COVERS = [
    backendCommerceCover,
    posInventoryCover,
    warrantyServiceCover,
    communityQuizCover,
    donationAnalyticsCover,
    fullstackServiceCover,
];

const getFallbackByHash = (project = {}) => {
    const key = `${project.id || project._id || ""}${project.name || "project"}`;
    const hash = [...key].reduce((sum, char) => sum + char.charCodeAt(0), 0);
    return FALLBACK_COVERS[hash % FALLBACK_COVERS.length];
};

const getProjectMeta = (project = {}) => {
    const searchableText = `${project.name || ""} ${project.type || ""} ${project.description || ""} ${(project.technology || []).join(" ")}`.toLowerCase();

    if (
        searchableText.includes("pos") ||
        searchableText.includes("inventory") ||
        searchableText.includes("sales") ||
        searchableText.includes("supplier")
    ) {
        return { cover: posInventoryCover, category: "POS & Inventory" };
    }

    if (
        searchableText.includes("e-commerce") ||
        searchableText.includes("ecommerce") ||
        searchableText.includes("checkout") ||
        searchableText.includes("order") ||
        searchableText.includes("cart")
    ) {
        return { cover: backendCommerceCover, category: "E-commerce" };
    }

    if (
        searchableText.includes("warranty") ||
        searchableText.includes("claim") ||
        searchableText.includes("qr code") ||
        searchableText.includes("service provider")
    ) {
        return { cover: warrantyServiceCover, category: "Warranty" };
    }

    if (
        searchableText.includes("quiz") ||
        searchableText.includes("leaderboard") ||
        searchableText.includes("publications") ||
        searchableText.includes("employee")
    ) {
        return { cover: communityQuizCover, category: "Quiz Platform" };
    }

    if (
        searchableText.includes("donation") ||
        searchableText.includes("mojo") ||
        searchableText.includes("products sold")
    ) {
        return { cover: donationAnalyticsCover, category: "Donation Analytics" };
    }

    if (
        searchableText.includes("travel") ||
        searchableText.includes("study") ||
        searchableText.includes("learning") ||
        searchableText.includes("react") ||
        searchableText.includes("firebase")
    ) {
        return { cover: fullstackServiceCover, category: "Full-Stack Service" };
    }

    return { cover: getFallbackByHash(project), category: "Web Platform" };
};

const ProjectCart = ({ project }) => {
    const { id, _id, name, type, liveSite, technology } = project;
    const { cover, category } = getProjectMeta(project);

    if (!project) {
        return <Loading />;
    }

    return (
        <div
            className="card card-compact w-full shadow-2xl"
            data-aos="zoom-in-up"
            data-aos-duration="2000"
        >
            <figure className="h-72 w-full overflow-hidden">
                <img
                    src={cover}
                    alt={name}
                    className="h-full w-full object-cover transition ease-in-out delay-150 border-orange-500 border-b hover:-translate-y-1 hover:scale-110 duration-300"
                />
            </figure>

            <div className="card-body">
                <h2 className="text-2xl font-semibold text-center text-orange-400">
                    {name}
                </h2>
                <p className="text-xs uppercase tracking-wider text-orange-300 text-center">
                    Full-Stack Developer (Backend-Focused)
                </p>
                <div className="flex justify-center mt-1">
                    <span className="badge-brand">{category}</span>
                </div>
                <p className="text-yellow-50 opacity-70">{type}</p>
                {Array.isArray(technology) && technology.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                        {technology.slice(0, 4).map((item, index) => (
                            <span
                                key={`${item}-${index}`}
                                className="text-xs px-2 py-1 rounded-full border border-orange-500 text-yellow-50 bg-slate-800"
                            >
                                {item}
                            </span>
                        ))}
                    </div>
                )}
                <div className="card-actions justify-center gap-4 my-2">
                    {liveSite ? (
                        <a
                            href={liveSite}
                            target="_blank"
                            className="btn-brand-secondary"
                            rel="noopener noreferrer"
                        >
                            Live Site <FaExternalLinkAlt className="ml-2" />
                        </a>
                    ) : (
                        <span className="btn-brand-secondary opacity-60 cursor-not-allowed">
                            Private / Enterprise
                        </span>
                    )}
                    <Link
                        to={`/projectDetails/${id || _id}`}
                        className="btn-brand-primary"
                    >
                        Details <ImFileText className="ml-2" />
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default ProjectCart;
